import React from "react";
import Avatar from "./Avatar";
import CrIcon from "./CrIcon";
import FancySelect from "./FancySelect";
import { dialog } from "../lib/dialog";

// Meeting notetaker (native Teams / Google Meet transcripts). The setting is
// settings.meetingNotetaker in the workspace doc; the backend normalizer
// (normalizeMeetingNotetakerSettings in server/utils/meetingNotetakerPolicy.js)
// is the authority. It refuses to turn the feature on unless the same save
// carries consentAccepted: true, and it stamps consentAcceptedAt/By itself.
//
// Transcript-access setup (Teams admin consent, per-organizer authorize) is
// server-owned and read from GET /api/meeting-notes/status. Nothing here
// writes it; the consent flows run in popups and the card re-reads status.
//
// House rule: no em dashes in any copy on this card.

// Same list as PUBLIC_WEBMAIL_DOMAINS on the server, so the hub never shows a
// domain the server would silently drop on save.
const PUBLIC_WEBMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "hotmail.co.uk",
  "live.com",
  "msn.com",
  "yahoo.com",
  "yahoo.co.uk",
  "ymail.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "gmx.com",
  "gmx.net",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "yandex.ru",
  "qq.com",
  "163.com",
  "fastmail.com",
  "hey.com",
  "tutanota.com",
  "comcast.net",
  "verizon.net",
  "att.net",
]);
const MAX_DOMAINS = 20;
const DOMAIN_RE =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** "@Acme.com " -> { domain: "acme.com" } or { error }. Mirrors the server. */
export function parseInternalDomain(value) {
  const d = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^@+/, "")
    .replace(/\.+$/, "");
  if (!d) return { error: "" };
  if (!DOMAIN_RE.test(d)) return { error: "Enter a domain like acme.com." };
  if (PUBLIC_WEBMAIL_DOMAINS.has(d)) {
    return {
      error:
        "Public email providers like gmail.com can't be internal, because anyone can have an address there.",
    };
  }
  return { domain: d };
}

/** The stored setting with defaults. Anything malformed reads as off. */
export function readNotetaker(value) {
  const v = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return {
    ...v,
    enabled: v.enabled === true,
    internalDomains: Array.isArray(v.internalDomains)
      ? v.internalDomains.filter((d) => typeof d === "string" && d)
      : [],
    externalNotes: v.externalNotes === "never" ? "never" : "approval",
    skipSensitiveTitles: v.skipSensitiveTitles !== false,
  };
}

const STATE_TONE = {
  ready: "ok",
  blocked_by_admin: "bad",
  tenant_changed: "warn",
  needs_attention: "warn",
  unavailable: "bad",
};
const STATE_LABEL = {
  ready: "Ready",
  not_connected: "Needs setup",
  work_account_required: "Needs a work account",
  admin_consent_needed: "Needs admin approval",
  tenant_changed: "Needs admin approval",
  blocked_by_admin: "Blocked by your admin",
  organizer_authorize_needed: "Organizer must allow",
  authorize_needed: "Organizer must allow",
  verification_pending: "Waiting for Google verification",
  needs_attention: "Needs attention",
  unavailable: "Unavailable",
};

const CONSENT_START_ERRORS = {
  connect_microsoft_first:
    "Connect the workspace owner's Microsoft work account first, then try again.",
  work_account_required:
    "The connected Microsoft account is a personal account. Teams transcripts need a Microsoft 365 work or school account.",
  microsoft_not_configured:
    "Microsoft sign-in is not set up on this BossDrop server. Contact BossDrop support.",
};

function lower(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

/**
 * Whose Google account the Meet organizer authorize starts on until the viewer
 * picks someone: the account BossDrop's own meetings are booked from
 * (status.google.organizerEmail), when this viewer may start it for that
 * account (an admin, or that person themself), else the viewer.
 */
export function defaultMeetOrganizer({ bookingEmail, currentUserEmail, canManageWorkspace } = {}) {
  const booking = lower(bookingEmail);
  const self = lower(currentUserEmail);
  return booking && (canManageWorkspace || booking === self) ? booking : self;
}

/**
 * Where to connect Google Calendar for Meet transcripts: the card of the
 * account BossDrop's own meetings are booked from. An owner who is not in
 * Team directory has no card there yet, so the hint says how to get one.
 */
export function connectGoogleHint({ bookingEmail, workspaceOwnerEmail, teamMembers } = {}) {
  const owner = lower(workspaceOwnerEmail);
  const booking = lower(bookingEmail) || owner;
  const listed = (Array.isArray(teamMembers) ? teamMembers : []).find(
    (m) => lower(m?.email) === booking,
  );
  if (booking && booking !== owner) {
    return `Connect Google Calendar on ${listed?.name || booking}'s card in Team directory.`;
  }
  if (listed || !owner) return "Connect Google Calendar from the owner's card in Team directory.";
  return `Connect Google Calendar on the owner's card in Team directory: add the owner (${owner}) there first. Or pick a Meeting calendar that has Google connected.`;
}

// Disconnect (server/meetingNotes/disconnects.js): Google Meet for the
// viewer's OWN account, Teams for admins (one approval covers the workspace).
export const MEETING_NOTES_DISCONNECT_CONFIRM = {
  google: {
    title: "Disconnect Google Meet transcripts for your Google account?",
    body:
      "BossDrop stops reading transcripts and attendance from Google Meet meetings you organize, and won't use this access again until you click Allow. " +
      "Google still lists the permission until you remove BossDrop in your Google Account, which also disconnects your calendar.",
  },
  teams: {
    title: "Disconnect Teams transcripts for this workspace?",
    body:
      "BossDrop forgets the Microsoft 365 approval and stops reading Teams transcripts and attendance. An admin can approve it again here. " +
      "The approval stays in Microsoft until a Microsoft 365 admin removes BossDrop under Enterprise applications.",
  },
};

/**
 * Which Disconnect buttons the card offers for this status and viewer. Only a
 * server that has the Disconnect routes reports google.mine, so an older one
 * is never offered a button it would answer with "not found".
 */
export function notesDisconnectOffers(status, canManageWorkspace) {
  const supported = !!status?.google?.mine;
  return {
    google: supported && status.google.mine.allowed === true,
    teams:
      supported &&
      !!canManageWorkspace &&
      !!status?.teams?.grantedAt &&
      status?.teams?.state !== "disconnected",
  };
}

/** POSTs a Disconnect; throws an Error carrying the server's code on failure. */
export async function disconnectMeetingNotes({ apiBase = "", provider, fetchImpl } = {}) {
  if (provider !== "google" && provider !== "teams") throw new Error("bad_provider");
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) throw new Error("meeting_notes_disconnect_failed");
  const base = String(apiBase || "").replace(/\/+$/, "");
  const r = await doFetch(`${base}/api/meeting-notes/${provider}/disconnect`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok || json?.ok === false) throw new Error(json?.error || "meeting_notes_disconnect_failed");
  return json;
}

// Open the popup synchronously in the click handler (popup blockers allow
// that), then point it at the real URL once it is known.
function openPopup(name) {
  try {
    return window.open("about:blank", name, "width=620,height=720");
  } catch {
    return null;
  }
}

function watchPopup(pop, onDone) {
  if (!pop) {
    onDone();
    return;
  }
  const t = setInterval(() => {
    if (!pop || pop.closed) {
      clearInterval(t);
      onDone();
    }
  }, 1000);
  setTimeout(() => clearInterval(t), 10 * 60 * 1000);
}

function SourceBlock({ title, icon, source, children }) {
  const state = source?.state || "unavailable";
  const tone = STATE_TONE[state] || "warn";
  return (
    <div
      className="panelSub"
      style={{ margin: 0, padding: 12, display: "grid", gap: 6 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <strong style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {icon}
          {title}
        </strong>
        <span className={`chip ${tone}`}>{STATE_LABEL[state] || "Needs setup"}</span>
      </div>
      <div className="muted small">{source?.message || ""}</div>
      {children}
    </div>
  );
}

export default function MeetingNotetakerCard({
  apiBase,
  value,
  onChange,
  canManageWorkspace,
  currentUserEmail,
  workspaceOwnerEmail,
  teamMembers,
  automationMode,
}) {
  const cfg = React.useMemo(() => readNotetaker(value), [value]);
  const base = String(apiBase || "").replace(/\/+$/, "");
  const [status, setStatus] = React.useState(null);
  const [statusState, setStatusState] = React.useState("loading");
  const [consentOpen, setConsentOpen] = React.useState(false);
  const [consentChecked, setConsentChecked] = React.useState(false);
  const [domainInput, setDomainInput] = React.useState("");
  const [domainError, setDomainError] = React.useState("");
  const [actionError, setActionError] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [organizer, setOrganizer] = React.useState(lower(currentUserEmail));
  // Google Meet's own pick, "" until the viewer chooses (defaultMeetOrganizer).
  const [meetOrganizer, setMeetOrganizer] = React.useState("");

  const refreshStatus = React.useCallback(async () => {
    try {
      const r = await fetch(`${base}/api/meeting-notes/status`, {
        credentials: "include",
        cache: "no-store",
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json?.error || "meeting_notes_status_failed");
      setStatus(json);
      setStatusState("ready");
    } catch {
      console.warn("meeting notes status failed");
      setStatus(null);
      setStatusState("error");
    }
  }, [base]);

  React.useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // The consent flows finish in another window, so re-read on focus too.
  React.useEffect(() => {
    const onFocus = () => refreshStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshStatus]);

  React.useEffect(() => {
    if (!organizer && currentUserEmail) setOrganizer(lower(currentUserEmail));
  }, [currentUserEmail, organizer]);

  // Whose account the organizer authorize runs for. A member can only
  // authorize their own calendar (canManageCalendarTarget on the server);
  // an admin can start it for anyone in the workspace, but the person who
  // signs in to Microsoft or Google must be that organizer.
  const organizerOptions = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    // The directory name for an address, so "You" and "Workspace owner" get
    // the same badge they have in the Team directory.
    const directoryName = new Map(
      (Array.isArray(teamMembers) ? teamMembers : [])
        .filter((m) => m?.email && m?.name)
        .map((m) => [lower(m.email), String(m.name)]),
    );
    const add = (email, label) => {
      const e = lower(email);
      if (!e || !e.includes("@") || seen.has(e)) return;
      seen.add(e);
      out.push({
        value: e,
        label: label ? `${label} (${e})` : e,
        icon: <Avatar name={directoryName.get(e) || ""} email={e} size={22} />,
      });
    };
    add(currentUserEmail, "You");
    if (canManageWorkspace) {
      add(workspaceOwnerEmail, "Workspace owner");
      for (const m of Array.isArray(teamMembers) ? teamMembers : []) {
        add(m?.email, m?.name || "");
      }
    }
    return out;
  }, [canManageWorkspace, currentUserEmail, teamMembers, workspaceOwnerEmail]);

  // Google Meet: the account BossDrop's own meetings are booked from comes
  // preselected, and an admin is offered it even when it is not listed above.
  const bookingAccount = lower(status?.google?.organizerEmail);
  const meetPick =
    meetOrganizer ||
    defaultMeetOrganizer({ bookingEmail: bookingAccount, currentUserEmail, canManageWorkspace });
  const meetOrganizerOptions = React.useMemo(() => {
    if (!canManageWorkspace || !bookingAccount || organizerOptions.some((o) => o.value === bookingAccount)) {
      return organizerOptions;
    }
    return [
      ...organizerOptions,
      {
        value: bookingAccount,
        label: `BossDrop's meeting calendar (${bookingAccount})`,
        icon: <Avatar name="" email={bookingAccount} size={22} />,
      },
    ];
  }, [bookingAccount, canManageWorkspace, organizerOptions]);

  const update = React.useCallback(
    (patch) => {
      onChange?.((prev) => ({ ...readNotetaker(prev), ...patch }));
    },
    [onChange],
  );

  function turnOn() {
    if (!canManageWorkspace || !consentChecked) return;
    // enabled and consentAccepted travel in the same save; the server refuses
    // one without the other and records who accepted and when.
    update({ enabled: true, consentAccepted: true });
    setConsentOpen(false);
    setConsentChecked(false);
    setTimeout(refreshStatus, 1500);
  }

  async function turnOff() {
    if (!canManageWorkspace) return;
    const ok = await dialog.confirm({
      title: "Turn off meeting notes?",
      body: "BossDrop stops reading transcripts for meetings from now on. Turning it back on asks for consent again.",
      confirmLabel: "Turn off",
    });
    if (!ok) return;
    update({ enabled: false, consentAccepted: false });
    setTimeout(refreshStatus, 1500);
  }

  // The switch. Off to on goes through the consent step, and stays off until it is accepted.
  function flipSwitch() {
    if (cfg.enabled) {
      turnOff();
      return;
    }
    setConsentOpen((open) => !open);
    setConsentChecked(false);
  }

  function addDomain() {
    const parsed = parseInternalDomain(domainInput);
    if (!parsed.domain) {
      setDomainError(parsed.error || "");
      return;
    }
    if (cfg.internalDomains.includes(parsed.domain)) {
      setDomainInput("");
      setDomainError("");
      return;
    }
    if (cfg.internalDomains.length >= MAX_DOMAINS) {
      setDomainError(`Up to ${MAX_DOMAINS} domains.`);
      return;
    }
    update({ internalDomains: [...cfg.internalDomains, parsed.domain] });
    setDomainInput("");
    setDomainError("");
  }

  async function startTeamsAdminConsent() {
    if (!canManageWorkspace || busy) return;
    setActionError("");
    setBusy("teams_admin");
    const pop = openPopup("bossdrop-notetaker-teams-admin");
    try {
      const r = await fetch(`${base}/api/meeting-notes/teams/consent/start`, {
        credentials: "include",
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok || !json?.url) {
        try {
          pop?.close();
        } catch {}
        setActionError(
          CONSENT_START_ERRORS[json?.error] ||
            "Couldn't start the Microsoft approval. Try again in a moment.",
        );
        setBusy("");
        await refreshStatus();
        return;
      }
      if (pop && !pop.closed) pop.location.href = json.url;
      else window.open(json.url, "_blank", "width=620,height=720");
      watchPopup(pop, () => {
        setBusy("");
        refreshStatus();
      });
    } catch {
      try {
        pop?.close();
      } catch {}
      setActionError("Couldn't start the Microsoft approval. Try again in a moment.");
      setBusy("");
    }
  }

  async function disconnectNotes(provider) {
    if (busy) return;
    const confirmed = await dialog.confirm({
      ...MEETING_NOTES_DISCONNECT_CONFIRM[provider],
      confirmLabel: "Disconnect",
      danger: true,
    });
    if (!confirmed) return;
    setActionError("");
    setBusy(`${provider}_disconnect`);
    try {
      await disconnectMeetingNotes({ apiBase: base, provider });
    } catch {
      setActionError(
        provider === "teams"
          ? "Couldn't disconnect Teams. Try again."
          : "Couldn't disconnect Google Meet. Try again.",
      );
    } finally {
      setBusy("");
      refreshStatus();
    }
  }

  function renderDisconnect(provider, label) {
    const working = busy === `${provider}_disconnect`;
    return (
      <div style={{ marginTop: 4 }}>
        <button
          type="button"
          className="btn ghost"
          disabled={!!busy}
          aria-busy={working}
          onClick={() => disconnectNotes(provider)}
        >
          {working ? "Disconnecting..." : label}
        </button>
      </div>
    );
  }

  function startOrganizerAuthorize(provider) {
    const target = lower(provider === "google" ? meetPick : organizer);
    if (!target || busy) return;
    setActionError("");
    setBusy(`${provider}_organizer`);
    const url =
      provider === "teams"
        ? `${base}/api/ms/oauth/start?feature=notetaker&email=${encodeURIComponent(target)}`
        : `${base}/api/google/oauth/start?mode=notetaker&email=${encodeURIComponent(target)}`;
    const pop = window.open(url, `bossdrop-notetaker-${provider}`, "width=620,height=720");
    watchPopup(pop, () => {
      setBusy("");
      refreshStatus();
    });
  }

  function renderOrganizerAuthorize(provider) {
    const label = provider === "teams" ? "Microsoft" : "Google";
    const isGoogle = provider === "google";
    const options = isGoogle ? meetOrganizerOptions : organizerOptions;
    const value = isGoogle ? meetPick : organizer;
    return (
      <div style={{ display: "grid", gap: 6, marginTop: 4 }}>
        <div className="muted small">
          The person who signs in to {label} must be the organizer you pick
          here.
        </div>
        <div
          style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
        >
          {options.length > 1 ? (
            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
              <FancySelect
                value={value}
                onChange={(v) => (isGoogle ? setMeetOrganizer(lower(v)) : setOrganizer(lower(v)))}
                options={options}
                ariaLabel="Meeting organizer"
                usePortal={true}
              />
            </div>
          ) : null}
          <button
            type="button"
            className="btn"
            disabled={!value || !!busy}
            onClick={() => startOrganizerAuthorize(provider)}
          >
            {busy === `${provider}_organizer`
              ? "Waiting for sign-in…"
              : options.length > 1
                ? `Allow with ${label}`
                : `Allow for my ${label} account`}
          </button>
        </div>
      </div>
    );
  }

  const teams = status?.teams || null;
  const google = status?.google || null;
  const offers = notesDisconnectOffers(status, canManageWorkspace);
  const anyReady = !!(teams?.ready || google?.ready);
  const enabled = cfg.enabled;
  const autoMode = automationMode === "auto";

  const needsSetup = enabled && statusState === "ready" && !anyReady;
  const headChip = !enabled
    ? { tone: "", label: "Off" }
    : needsSetup
      ? { tone: "warn", label: "On, needs setup" }
      : { tone: "ok", label: "On" };

  return (
    <div className="panelSub" style={{ marginTop: "var(--card-gap)" }}>
      <div className="crCardHead">
        <div className="crCardHead__main">
          <div className="ruleTitle crCardHead__title">
            <CrIcon name="meetings" />
            Meeting notes
          </div>
        </div>
        <div className="crCardHead__aside">
          <span className={`chip ${headChip.tone}`}>{headChip.label}</span>
          <input
            className="switchInput"
            type="checkbox"
            checked={enabled}
            disabled={!canManageWorkspace}
            onChange={flipSwitch}
            aria-label="Toggle Meeting notes"
          />
        </div>
      </div>
      <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
        <div className="muted small" style={{ maxWidth: "88ch" }}>
          For meetings BossDrop books, BossDrop reads the Teams or Google Meet
          transcript after the meeting ends, writes notes, emails them to the
          people who joined, and creates the follow-up tasks. No bot joins the
          call. It is off until a workspace admin turns it on.
        </div>

        {/* On / off, with the consent step in plain words. */}
        {enabled ? (
          <div style={{ display: "grid", gap: 6 }}>
            <div className="muted small">
              Every invite BossDrop sends says the transcript is used for notes,
              and each invitee gets an email from ai@bossdrop.ai with a personal
              opt-out link. If anyone opts out, that meeting's transcript is
              never read and the organizer is told.
            </div>
            {cfg.consentAcceptedAt ? (
              <div className="muted small">
                Turned on
                {cfg.consentAcceptedBy ? ` by ${cfg.consentAcceptedBy}` : ""} on{" "}
                {new Date(cfg.consentAcceptedAt).toLocaleString()}.
              </div>
            ) : null}
          </div>
        ) : consentOpen && canManageWorkspace ? (
          <div className="infoBanner" style={{ display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 700 }}>Before you turn on meeting notes</div>
            <ul
              className="muted small"
              style={{ margin: 0, paddingInlineStart: 18, display: "grid", gap: 4 }}
            >
              <li>
                BossDrop reads the transcript of meetings it books (from the
                assistant or the Scheduler page) after they end. Teams and Google
                Meet only make a transcript when someone starts one, or when the
                meeting is set to transcribe automatically.
              </li>
              <li>
                Every invite says the transcript is used for notes. Each invitee
                gets their own email from ai@bossdrop.ai with an opt-out link that
                needs no login. One opt-out means BossDrop never reads that
                meeting's transcript, and the organizer is told.
              </li>
              <li>
                Transcripts are stored encrypted and deleted after 30 days. Notes
                are kept.
              </li>
              <li>
                Notes go to internal attendees who joined. People from outside
                your company get notes only after approval, with internal-only
                parts removed.
              </li>
              <li>
                Follow-up tasks are created following your Approvals setting.
                Closing tasks, status changes, escalations, anything for people
                outside your company, and HR or finance items always wait for
                approval.
              </li>
              <li>
                Meetings with HR, legal, 1:1 or pay-related titles, meetings
                starting in less than 15 minutes, and meetings with more than 50
                invitees are skipped.
              </li>
              <li>
                Recording and transcription laws differ by place, and some require
                everyone's agreement. You are responsible for making sure using
                transcripts is allowed for your meetings.
              </li>
            </ul>
            <label
              className="small"
              style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
            >
              <input
                className="bd-check"
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I understand how meeting transcripts are used, and I agree on
                behalf of this workspace.
              </span>
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn primary"
                disabled={!consentChecked}
                onClick={turnOn}
              >
                Accept and turn on
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setConsentOpen(false);
                  setConsentChecked(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : canManageWorkspace ? null : (
          <div className="muted small">
            Off. Only a workspace admin can turn it on.
          </div>
        )}

        {/* Options. Saved with the rest of the Control Room settings. */}
        <fieldset
          disabled={!canManageWorkspace}
          style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0, display: "grid", gap: 12 }}
        >
          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 600 }}>Internal domains</div>
            <div className="muted small">
              Addresses on your Team directory, on the workspace owner's company
              domain, or on these domains count as internal. Everyone else is a
              guest.
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                className="inputFancy"
                type="text"
                placeholder="yourcompany.com"
                value={domainInput}
                onChange={(e) => {
                  setDomainInput(e.target.value);
                  if (domainError) setDomainError("");
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  addDomain();
                }}
                style={{ flex: "1 1 220px", minWidth: 0 }}
                aria-label="Internal domain"
              />
              <button
                type="button"
                className="btn primary"
                disabled={!domainInput.trim()}
                onClick={addDomain}
                style={{ flexShrink: 0 }}
              >
                Add
              </button>
            </div>
            {domainError ? (
              <div className="small" style={{ color: "var(--bad, #c0392b)" }}>
                {domainError}
              </div>
            ) : null}
            {cfg.internalDomains.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {cfg.internalDomains.map((d) => (
                  <div key={d} className="chip crValueChip">
                    <span>{d}</span>
                    <button
                      type="button"
                      className="crValueChip__x"
                      aria-label={`Remove ${d}`}
                      onClick={() =>
                        update({
                          internalDomains: cfg.internalDomains.filter((x) => x !== d),
                        })
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 600 }}>Notes for guests</div>
            <div className="formRow checks checksPills" style={{ margin: 0 }}>
              <label
                className="toolOption"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <input
                  type="radio"
                  name="meetingNotetakerExternalNotes"
                  value="approval"
                  checked={cfg.externalNotes === "approval"}
                  onChange={() => update({ externalNotes: "approval" })}
                />
                <span>
                  <strong>After approval</strong>
                  <div className="muted small">
                    Guests who joined get a version without internal-only parts,
                    once it is approved in Audit Logs.
                  </div>
                </span>
              </label>
              <label
                className="toolOption"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <input
                  type="radio"
                  name="meetingNotetakerExternalNotes"
                  value="never"
                  checked={cfg.externalNotes === "never"}
                  onChange={() => update({ externalNotes: "never" })}
                />
                <span>
                  <strong>Never</strong>
                  <div className="muted small">
                    Notes only ever go to internal attendees.
                  </div>
                </span>
              </label>
            </div>
          </div>

          <label className="small" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <input
              className="bd-check"
              type="checkbox"
              checked={cfg.skipSensitiveTitles}
              onChange={(e) => update({ skipSensitiveTitles: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>Skip sensitive meetings</strong>
              <div className="muted small">
                No notes for meetings whose title looks like HR, legal, a 1:1,
                performance or pay.
              </div>
            </span>
          </label>
        </fieldset>

        <div className="muted small">
          Follow-up tasks:{" "}
          {autoMode
            ? "Approvals is set to Automated, so tasks are created right away unless that kind of action is set to require approval."
            : "Approvals is set to Manual, so every follow-up waits for approve or deny in Audit Logs."}{" "}
          Closing tasks, status changes, escalations, anything for guests, and
          HR or finance items always wait for approval.
        </div>

        {/* Transcript access. Server-owned, read-only here apart from the
            consent buttons, which open the provider's own sign-in. */}
        <div style={{ display: "grid", gap: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: 600 }}>Transcript access</div>
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setStatusState("loading");
                refreshStatus();
              }}
              disabled={statusState === "loading"}
            >
              {statusState === "loading" ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          {needsSetup ? (
            <div className="muted small">
              <span className="chip warn">Needs setup</span> Meeting notes are
              on, but BossDrop can't read Teams or Google Meet transcripts yet.
              Invites still carry the notice, and nothing is read until one of
              the steps below is done.
            </div>
          ) : null}
          {actionError ? (
            <div className="small" style={{ color: "var(--bad, #c0392b)" }}>
              {actionError}
            </div>
          ) : null}
          {statusState === "error" ? (
            <div className="muted small">
              Couldn't load transcript access. Try Refresh.
            </div>
          ) : !status ? (
            <div className="muted small">Loading transcript access…</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              <SourceBlock title="Microsoft Teams" source={teams}>
                {teams?.action === "admin_consent" ? (
                  canManageWorkspace ? (
                    <div style={{ display: "grid", gap: 6, marginTop: 4 }}>
                      <div className="muted small">
                        Opens Microsoft sign-in. Sign in there as a Microsoft 365
                        admin for your organization. It can be a different
                        Microsoft account from yours.
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn"
                          disabled={!!busy}
                          onClick={startTeamsAdminConsent}
                        >
                          {busy === "teams_admin"
                            ? "Waiting for Microsoft…"
                            : "Get Microsoft 365 admin approval"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="muted small">
                      Ask a workspace admin to start the Microsoft 365 approval.
                    </div>
                  )
                ) : null}
                {teams?.action === "organizer_authorize"
                  ? renderOrganizerAuthorize("teams")
                  : null}
                {teams?.action === "connect_microsoft" ? (
                  <div className="muted small">
                    Connect Microsoft from the owner's card in Team directory.
                  </div>
                ) : null}
                {teams?.ready ? (
                  <div className="muted small">
                    Organizers: start transcription in the meeting, or turn on
                    "Record and transcribe automatically" in the Teams meeting
                    options.
                  </div>
                ) : null}
                {offers.teams ? renderDisconnect("teams", "Disconnect Teams") : null}
              </SourceBlock>
              <SourceBlock title="Google Meet" source={google}>
                {google?.action === "organizer_authorize"
                  ? renderOrganizerAuthorize("google")
                  : null}
                {google?.action === "connect_google" ? (
                  <div className="muted small">
                    {connectGoogleHint({
                      bookingEmail: google?.organizerEmail,
                      workspaceOwnerEmail,
                      teamMembers,
                    })}
                  </div>
                ) : null}
                {google?.ready ? (
                  <div className="muted small">
                    Other organizers who book Google Meet meetings can allow
                    access for their own account here too.
                    {renderOrganizerAuthorize("google")}
                  </div>
                ) : null}
                {offers.google
                  ? renderDisconnect("google", "Disconnect my Google Meet access")
                  : null}
                {google?.mine?.disconnected ? (
                  <div className="muted small">
                    You disconnected Google Meet transcripts for your Google
                    account. Allow it again for your account to turn them back
                    on.
                    {google?.action === "organizer_authorize" || google?.ready
                      ? null
                      : renderOrganizerAuthorize("google")}
                  </div>
                ) : null}
              </SourceBlock>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
